import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Settings as SettingsIcon, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  MapPin,
  Mail,
  Key,
  Loader,
  MessageSquare
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SlackConnection from './SlackConnection';
import SimpleGmailIntegration from './SimpleGmailIntegration';

const Settings = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    locations: [],
    primary_location: 'All Locations'
  });

  const { user } = useAuth();

  // Load users and locations
  useEffect(() => {
    if (user?.role === 'admin') {
      loadData(); // Load both users and locations for admins
    } else if (user?.role === 'manager') {
      loadLocations(); // Load only locations for managers
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load users and locations
      const [usersData, locationsData] = await Promise.all([
        apiService.getUsers(),
        apiService.getLocations()
      ]);
      
      setUsers(usersData);
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading settings data:', error);
      setError('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    setLoading(true);
    try {
      // Load only locations for managers
      const locationsData = await apiService.getLocations();
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading locations:', error);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      setError('Email, password, and name are required');
      return;
    }

    try {
      await apiService.createUser(newUser);
      
      // Reset form
      setNewUser({
        email: '',
        password: '',
        name: '',
        role: 'viewer',
        locations: [],
        primary_location: 'All Locations'
      });
      setShowAddUser(false);
      
      // Reload users list
      await loadData();
      
      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error('Error adding user:', error);
      const errorMessage = error.message || 'Failed to add user';
      setError(errorMessage);
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser({
      id: userToEdit.id,
      name: userToEdit.name,
      email: userToEdit.email,
      role: userToEdit.role,
      locations: userToEdit.locations || [],
      primary_location: userToEdit.primary_location || 'All Locations',
      password: '' // Don't show existing password
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser.email || !editingUser.name) {
      setError('Email and name are required');
      return;
    }

    try {
      // Prepare update data - only include password if it's been changed
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        locations: editingUser.locations,
        primary_location: editingUser.primary_location
      };

      // Only include password if user entered one
      if (editingUser.password && editingUser.password.trim() !== '') {
        updateData.password = editingUser.password;
      }

      await apiService.updateUser(editingUser.id, updateData);
      
      setEditingUser(null);
      setShowEditUser(false);
      
      // Reload users list
      await loadData();
      
      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.message || 'Failed to update user';
      setError(errorMessage);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (userId === user.id) {
      setError('Cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingUserId(userId);
      await apiService.deleteUser(userId);
      
      // Reload users list
      await loadData();
      
      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.message || 'Failed to delete user';
      setError(errorMessage);
    } finally {
      setDeletingUserId(null);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-300';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'viewer': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Check if user has any access to settings (admin or manager)
  if (!user || (user.role !== 'admin' && user.role !== 'manager')) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <Card className="p-8 text-center max-w-md">
            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You need administrator or manager privileges to access settings.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <SettingsIcon className="w-8 h-8 text-slate-600" />
            <h1 className="text-3xl font-bold text-slate-800">Settings</h1>
          </div>
          <p className="text-gray-600 mt-2">Manage system settings and user accounts</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2 text-red-600 hover:text-red-700"
            onClick={() => setError(null)}
          >
            ×
          </Button>
        </div>
      )}

      <Tabs defaultValue={user?.role === 'admin' ? "users" : "integrations"} className="space-y-6">
        <TabsList className={`grid w-full ${user?.role === 'admin' ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {user?.role === 'admin' && (
            <TabsTrigger value="users">User Management</TabsTrigger>
          )}
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        {user?.role === 'admin' && (
          <TabsContent value="users" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
              <p className="text-gray-600">Manage user accounts and permissions</p>
            </div>
            <Button onClick={() => setShowAddUser(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-600" />
                <p className="text-gray-600">Loading users...</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              {users.map((user) => (
                <Card key={user.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{user.email}</span>
                            </div>
                            <Badge className={getRoleColor(user.role)}>
                              {user.role}
                            </Badge>
                            {user.primary_location && (
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span className="font-medium">Primary: {user.primary_location}</span>
                              </div>
                            )}
                            {user.locations.length > 0 && (
                              <div className="flex items-center space-x-1 text-gray-500">
                                <span>{user.locations.length} accessible location(s)</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.role !== 'admin' && ( // Only show delete for non-admin users
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            disabled={deletingUserId === user.id}
                            title="Delete User"
                          >
                            {deletingUserId === user.id ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </TabsContent>
        )}

        <TabsContent value="locations" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Locations</h2>
            <p className="text-gray-600">Manage Bond Collective locations</p>
          </div>
          
          <div className="grid gap-4">
            {locations.map((location) => (
              <Card key={location.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{location.name}</h3>
                      <p className="text-gray-600">{location.address}</p>
                      <p className="text-sm text-gray-500">Manager: {location.manager}</p>
                    </div>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
            <p className="text-gray-600">Manage third-party integrations and connections</p>
          </div>
          
          {/* Gmail Integration Card - Available to managers and admins */}
          {(user?.role === 'manager' || user?.role === 'admin') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  Gmail Integration
                  <Badge className="ml-2 bg-blue-100 text-blue-800">Manager & Admin</Badge>
                </CardTitle>
                <div className="px-6 pb-2">
                  <p className="text-sm text-gray-600">
                    Connect your personal Gmail account to automatically track email communications with CRM contacts and companies
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <SimpleGmailIntegration />
              </CardContent>
            </Card>
          )}

          {/* Gmail Integration Not Available for Viewers */}
          {user?.role === 'viewer' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-gray-400" />
                  Gmail Integration
                  <Badge className="ml-2 bg-gray-100 text-gray-600">Requires Manager Role</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-center">
                    <Mail className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600 font-medium">Gmail Integration Unavailable</p>
                    <p className="text-sm text-gray-500 mt-1">
                      You need manager or admin privileges to connect Gmail accounts for email tracking
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Slack Integration Card - Admin Only */}
          {user?.role === 'admin' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Slack Integration
                  <Badge className="ml-2 bg-red-100 text-red-800">Admin Only</Badge>
                </CardTitle>
                <div className="px-6 pb-2">
                  <p className="text-sm text-gray-600">
                    Configure organization-wide Slack notifications for deal updates, new leads, and team collaboration
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <SlackConnection />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="system" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
            <p className="text-gray-600">Configure system-wide settings</p>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>HubSpot Integration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="hubspot-key">HubSpot API Key</Label>
                <Input
                  id="hubspot-key"
                  type="password"
                  placeholder="Enter HubSpot API key"
                />
              </div>
              <Button>Save Integration Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="userEmail">Email Address *</Label>
              <Input
                id="userEmail"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="user@bondcollective.com"
              />
            </div>
            <div>
              <Label htmlFor="userName">Full Name *</Label>
              <Input
                id="userName"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label htmlFor="userPassword">Password *</Label>
              <Input
                id="userPassword"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label htmlFor="userRole">Role</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="primaryLocation">Primary Location</Label>
              <Select value={newUser.primary_location} onValueChange={(value) => setNewUser({...newUser, primary_location: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select primary location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Locations">All Locations</SelectItem>
                  {locations.map((location) => (
                    <SelectItem key={location.id || location.name} value={location.name || location}>
                      {location.name || location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                This location will be the default view for this user across all pages
              </p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddUser} className="flex-1">
                Add User
              </Button>
              <Button variant="outline" onClick={() => setShowAddUser(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editUserName">Full Name *</Label>
              <Input
                id="editUserName"
                type="text"
                placeholder="Enter full name"
                value={editingUser?.name || ''}
                onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="editUserEmail">Email Address *</Label>
              <Input
                id="editUserEmail"
                type="email"
                placeholder="Enter email address"
                value={editingUser?.email || ''}
                onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="editUserPassword">New Password (leave blank to keep current)</Label>
              <Input
                id="editUserPassword"
                type="password"
                placeholder="Enter new password"
                value={editingUser?.password || ''}
                onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="editUserRole">Role *</Label>
              <Select
                value={editingUser?.role || 'viewer'}
                onValueChange={(value) => setEditingUser({...editingUser, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="editPrimaryLocation">Primary Location</Label>
              <Select 
                value={editingUser?.primary_location || 'All Locations'} 
                onValueChange={(value) => setEditingUser({...editingUser, primary_location: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select primary location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Locations">All Locations</SelectItem>
                  {locations.map((location) => (
                    <SelectItem key={location.id || location.name} value={location.name || location}>
                      {location.name || location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                This location will be the default view for this user across all pages
              </p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleUpdateUser} className="flex-1">
                Update User
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditUser(false);
                  setEditingUser(null);
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;